import React, { useCallback, useEffect, useState } from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { eventsAPI, familyAPI, travelAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { AttendanceStatus } from '../../types/event';
import type { FamilyResponse } from '../../types/family';
import type { EventTravel } from '../../types/travel';
import PlayerTravelChoice from '../travel/PlayerTravelChoice';
import MyCarPanel from '../travel/MyCarPanel';
import { aboardMyCar, useTravelResponder } from '../travel/useTravelResponder';
import { shortNames } from '../family/format';
import { getErrorMessage } from '../../utils/errorMessage';

interface EventResponsePanelProps {
  eventId: number;
  /**
   * A saját játékosok a visszajelzésükkel (pl. a családi programból). Ha nincs
   * megadva, a panel maga tölti be - a kerettel és a kikérőkkel együtt.
   */
  players?: FamilyResponse[];
  /** Látszik-e a kikérő kérése (a "Jön" után, gyerekenként). */
  excuseLetterEnabled?: boolean;
  /** "Jön" után rákérdez az utazásra is. */
  withTravel?: boolean;
  /** Több gyereknél a nevük is látszik; alapból akkor, ha egynél több van. */
  showNames?: boolean;
  /** Ha a többi nézet (utazás, névsor) frissítendő, ez nő. */
  travelRefreshKey?: number;
  /** Az utazás itt megváltozott. */
  onTravelChanged?: () => void;
  /** Egy játékos visszajelzése megváltozott (a szerver által visszaigazolt állapottal). */
  onStatusChange?: (playerId: number, status: AttendanceStatus) => void;
}

/**
 * Egy esemény visszajelzése gyerekenként egy kártyán: jön / nem jön, és ha
 * jön, hogyan jut el (fuvar kell, saját maga megy, autóval megyünk), valamint
 * a kikérő. Aki nincs a meccs keretében, látja az eseményt, de nem jelezhet.
 */
const EventResponsePanel: React.FC<EventResponsePanelProps> = ({
  eventId,
  players: initialPlayers,
  excuseLetterEnabled = false,
  withTravel = true,
  showNames,
  travelRefreshKey = 0,
  onTravelChanged,
  onStatusChange,
}) => {
  const { showToast } = useToast();
  const [players, setPlayers] = useState<FamilyResponse[] | null>(initialPlayers ?? null);
  const [savingFor, setSavingFor] = useState<number | null>(null);
  const [excuseBusy, setExcuseBusy] = useState<number | null>(null);
  const [travel, setTravel] = useState<EventTravel | null>(null);
  const responder = useTravelResponder(travel, setTravel, onTravelChanged);

  // A kívülről kapott állapot (pl. a program frissült) felülírja a helyit
  const initialKey = initialPlayers?.map((p) => `${p.playerId}:${p.status}:${p.inSquad}`).join(',');
  useEffect(() => {
    if (initialPlayers) setPlayers(initialPlayers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialKey]);

  const loadPlayers = useCallback(async () => {
    try {
      const loaded = await familyAPI.getEventPlayers(eventId);
      setPlayers(loaded);
      return loaded;
    } catch {
      return null;
    }
  }, [eventId]);

  useEffect(() => {
    if (!initialPlayers) loadPlayers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const anyAttending = (players ?? []).some((p) => p.inSquad && p.status === AttendanceStatus.Attending);

  const loadTravel = useCallback(async () => {
    try {
      const loaded = await travelAPI.getTravel(eventId);
      setTravel(loaded);
      return loaded;
    } catch {
      setTravel(null);
      return null;
    }
  }, [eventId]);

  useEffect(() => {
    if (withTravel && anyAttending) loadTravel();
  }, [withTravel, anyAttending, loadTravel, travelRefreshKey]);

  if (!players || players.length === 0) return null;

  const names = shortNames(players);
  const named = showNames ?? players.length > 1;

  const respond = async (player: FamilyResponse, status: AttendanceStatus) => {
    if (player.status === status || savingFor != null) return;
    const previous = player.status;
    const wasInMyCar = !!travel && aboardMyCar(travel).some((p) => p.playerId === player.playerId);
    setPlayers((prev) => prev && prev.map((p) => (p.playerId === player.playerId ? { ...p, status } : p)));
    setSavingFor(player.playerId);
    try {
      const saved = await eventsAPI.submitResponse(eventId, { status, playerId: player.playerId });
      // A szerver szerinti állapot számít - ha valami mást rögzített, azt mutatjuk
      const confirmed = (await loadPlayers())?.find((p) => p.playerId === player.playerId)?.status ?? saved.status;
      if (confirmed !== status) {
        setPlayers((prev) => prev && prev.map((p) => (p.playerId === player.playerId ? { ...p, status: confirmed } : p)));
      }
      onStatusChange?.(player.playerId, confirmed);

      // "Nem jön" után az utazásból is kikerül; ha az én autómban ült, és az kiürült, az autó is megy
      if (withTravel && status === AttendanceStatus.NotAttending && travel) {
        const updated = await loadTravel();
        if (updated && wasInMyCar) await responder.dropEmptyCar(updated);
      }
      onTravelChanged?.();
    } catch (err: any) {
      setPlayers((prev) => prev && prev.map((p) => (p.playerId === player.playerId ? { ...p, status: previous } : p)));
      showToast(getErrorMessage(err, 'Nem sikerült elküldeni, próbáld újra.'), 'error');
    } finally {
      setSavingFor(null);
    }
  };

  const requestExcuse = async (playerId: number) => {
    setExcuseBusy(playerId);
    try {
      await eventsAPI.requestExcuseLetter(eventId, playerId);
      setPlayers((prev) => prev && prev.map((p) => (p.playerId === playerId ? { ...p, excuseLetterRequested: true } : p)));
      showToast('Kikérő igényelve. Ha elkészül, a Dokumentumok között találod.');
    } catch (err: any) {
      showToast(getErrorMessage(err, 'A kikérő igénylése nem sikerült.'), 'error');
    } finally {
      setExcuseBusy(null);
    }
  };

  const travelPlayers = new Set((travel?.myPlayers ?? []).map((p) => p.playerId));

  return (
    <div className="space-y-3">
      {players.map((p) => {
        const coming = p.status === AttendanceStatus.Attending;
        const notComing = p.status === AttendanceStatus.NotAttending;
        const saving = savingFor === p.playerId;
        const showTravel = withTravel && coming && !saving && travel?.canRespond && travelPlayers.has(p.playerId);

        return (
          <div key={p.playerId} className={players.length > 1 ? 'rounded-xl border border-gray-200 bg-white p-3' : ''}>
            {named && <p className="mb-2 text-[15px] font-semibold text-gray-900">{names[p.playerId]}</p>}

            {!p.inSquad ? (
              <p className="rounded-lg bg-gray-50 px-3 py-2.5 text-sm text-gray-600">
                Most nincs a mérkőzés keretében, ezért nem kell visszajelezni. Az edző értesít, ha változik.
              </p>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => respond(p, AttendanceStatus.Attending)}
                    disabled={saving}
                    aria-pressed={coming}
                    aria-label={`${p.name}: jön`}
                    className={`flex h-11 items-center justify-center gap-1.5 rounded-xl text-[15px] font-semibold transition-colors ${
                      coming ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {coming && <CheckIcon className="h-5 w-5" />}
                    Jön
                  </button>
                  <button
                    type="button"
                    onClick={() => respond(p, AttendanceStatus.NotAttending)}
                    disabled={saving}
                    aria-pressed={notComing}
                    aria-label={`${p.name}: nem jön`}
                    className={`flex h-11 items-center justify-center gap-1.5 rounded-xl text-[15px] font-semibold transition-colors ${
                      notComing ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {notComing && <XMarkIcon className="h-5 w-5" />}
                    Nem jön
                  </button>
                </div>

                {showTravel && travel && (
                  <div className="mt-3 border-t border-gray-100 pt-3">
                    <p className="mb-2 text-sm font-semibold text-gray-900">Hogyan jut el?</p>
                    <PlayerTravelChoice
                      travel={travel}
                      playerId={p.playerId}
                      busy={responder.busy}
                      onSet={(request) => responder.setPlayer(p.playerId, request)}
                      onChooseCar={() => responder.chooseCar(p.playerId)}
                      carPending={responder.pendingCarFor === p.playerId}
                      onCancelCar={responder.cancelPendingCar}
                    />
                  </div>
                )}

                {excuseLetterEnabled && coming && !saving && (
                  <div className="mt-3 flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
                    <p className="flex min-w-0 items-center gap-2 text-sm text-gray-700">
                      <DocumentTextIcon className="h-5 w-5 shrink-0 text-amber-600" />
                      {p.excuseLetterRequested
                        ? 'Kikérő igényelve - ha elkészül, a Dokumentumok között találod.'
                        : 'Kikérő az iskolából'}
                    </p>
                    {!p.excuseLetterRequested && (
                      <button
                        type="button"
                        onClick={() => requestExcuse(p.playerId)}
                        disabled={excuseBusy === p.playerId}
                        className="shrink-0 rounded-full bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                      >
                        Kérem
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}

      {withTravel && travel?.canRespond && <MyCarPanel travel={travel} responder={responder} />}
    </div>
  );
};

export default EventResponsePanel;
